<script setup>
import { onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import FileIcon from 'dashboard/components-next/icon/FileIcon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const navigateToNewDraft = async () => {
  const newDraft = await store.dispatch('drafts/create', {});
  const { accountId } = route.params;
  router.push({
    name: 'drafts_compose',
    params: { accountId, id: newDraft.id },
  });
};

const openDraftCompose = async draftId => {
  const { accountId } = route.params;
  router.push({
    name: 'drafts_compose',
    params: { accountId, id: draftId },
  });
};

const openConversation = async (conversationId, event) => {
  event?.stopPropagation();
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: conversationId,
    })
  );
  router.push({ path });
};

const destroyDraft = async draftId => {
  await store.dispatch('drafts/delete', draftId);
  useAlert(t('DRAFTS.DESTROY_SUCCESS'));
};

const draftsByInbox = computed(() => {
  let allDrafts = store.getters['drafts/getDrafts'];
  return allDrafts.reduce((acc, draft) => {
    const group = draft.inbox?.id || 'none';
    if (!acc[group]) acc[group] = { inbox: draft.inbox, items: [] };
    acc[group].items.push(draft);
    return acc;
  }, {});
});

onMounted(() => {
  store.dispatch('drafts/get');
});
</script>

<template>
  <div class="h-full overflow-y-auto">
    <div class="flex flex-col h-full">
      <div class="flex flex-col flex-shrink-0 h-full">
        <div
          class="flex items-center justify-between gap-2 px-3 h-12 border-b border-b-n-weak"
        >
          <div class="flex items-center justify-center min-w-0">
            <h1
              class="text-base font-medium truncate text-n-slate-12"
              :title="$t('SIDEBAR.DRAFTS')"
            >
              {{ $t('SIDEBAR.DRAFTS') }}
            </h1>
          </div>
          <div class="flex items-center justify-center min-w-0">
            <NextButton sm @click="navigateToNewDraft">
              {{ $t('DRAFTS.COMPOSE_BUTTON') }}
            </NextButton>
          </div>
        </div>
        <main
          class="flex overflow-y-auto overflow-x-hidden grow w-full bg-n-background p-4"
        >
          <ul v-if="Object.keys(draftsByInbox).length" class="w-full list-none">
            <li
              v-for="draftsInbox in draftsByInbox"
              :key="draftsInbox"
              class="bg-n-slate-3 rounded-md p-2 mb-4"
            >
              <h4
                class="text-base font-medium text-n-slate-12 mb-2 py-2 flex items-center ps-3"
              >
                <Avatar
                  :name="draftsInbox.inbox?.name || $t('DRAFTS.NO_INBOX')"
                  :src="draftsInbox.inbox?.avatar_url"
                  :size="24"
                  class="mr-3"
                  hide-offline-status
                  rounded-full
                />
                {{ draftsInbox.inbox?.name || $t('DRAFTS.NO_INBOX') }}
              </h4>
              <ul class="divide-y divide-n-weak w-full list-none">
                <li
                  v-for="draft in draftsInbox.items"
                  :key="draft.id"
                  class="p-2 cursor-pointer shadow outline-1 outline outline-n-container group/cardLayout rounded-md bg-n-solid-2"
                  @click="openDraftCompose(draft.id)"
                >
                  <div class="flex flex-row w-full gap-4 items-center">
                    <div
                      v-tooltip.top="$t('DRAFTS.ATTRS.TO')"
                      class="flex-shrink-0 flex flex-row items-center min-w-[12rem]"
                    >
                      <Avatar
                        :name="
                          draft.contact?.name || draft.contact?.email || '#'
                        "
                        :src="draft.contact?.thumbnail"
                        :size="36"
                        class="mr-4"
                        hide-offline-status
                        rounded-full
                      />
                      <span class="text-sm truncate max-w-[8rem]">
                        {{
                          draft.contact?.name ||
                          draft.contact?.email ||
                          draft.meta?.to_email ||
                          t('DRAFTS.NO_CONTACT')
                        }}
                      </span>
                    </div>
                    <div class="flex-auto min-w-0">
                      <p class="text-sm mb-0 truncate split-words">
                        {{ draft.meta?.subject || $t('DRAFTS.NO_SUBJECT') }}
                      </p>
                      <p
                        class="text-xs mb-0 truncate split-words text-n-slate-11"
                      >
                        {{
                          draft.content?.substring(0, 100) ||
                          $t('DRAFTS.EMPTY_DRAFTS')
                        }}
                      </p>
                    </div>
                    <div
                      v-if="draft.conversation"
                      v-tooltip.top="$t('DRAFTS.ATTRS.OPEN_CONVERSATION')"
                      class="flex flex-col items-end ms-auto"
                    >
                      <button
                        @click="openConversation(draft.conversation.id, $event)"
                      >
                        <Icon icon="i-lucide-message-square-share" />
                      </button>
                    </div>
                    <div class="items-start flex flex-row w-fit">
                      <div
                        v-if="draft.files?.length"
                        class="flex flex-row items-center gap-2 text-xs border border-woot-500 rounded-full px-2 py-1 w-max text-woot-700 bg-woot-50"
                      >
                        <FileIcon :file-type="draft.files[0].content_type" />
                        <span class="truncate max-w-[10rem]">
                          {{ draft.files[0].filename }}
                        </span>
                      </div>
                      <span
                        v-if="draft.files?.length > 1"
                        class="border border-slate-500 rounded-full text-slate-700 bg-slate-50 w-6 h-6 flex items-center justify-center text-xs ml-1 p-1"
                      >
                        {{ `+${draft.files.length - 1}` }}
                      </span>
                    </div>
                    <div class="flex flex-col items-end ms-auto min-w-[9rem]">
                      <span
                        v-tooltip.top="$t('DRAFTS.ATTRS.LAST_UPDATED')"
                        class="text-xs font-normal text-n-slate-11"
                      >
                        {{ new Date(draft.updated_at).toLocaleString() }}
                      </span>
                    </div>
                    <div>
                      <NextButton
                        ruby
                        sm
                        confirmation
                        :label="t('DRAFTS.DESTROY_BUTTON')"
                        @click.stop="destroyDraft(draft.id)"
                      />
                    </div>
                  </div>
                </li>
              </ul>
            </li>
          </ul>
          <div
            v-else
            class="flex flex-col items-center justify-center w-full h-full text-n-slate-11"
          >
            <Icon icon="i-lucide-file-text" class="text-4xl text-n-slate-7" />
            <p class="mt-4 text-center">
              {{ $t('DRAFTS.EMPTY_DRAFTS') }}
            </p>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>
